import React, {useEffect, useRef, useState} from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    Box,
    Stepper,
    Step,
    StepLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tooltip
} from "@mui/material";
import {colorPaper, colorWarningBackground, styleSecondaryButton} from "../../App/theme.js";
import LinearProgress from "@mui/material/LinearProgress";
import { deepClone, isMobile, parseJSON } from "../../App/utils.js";
import { createEmbeddingItem } from "../../API/OpenAIAPI.js";
import { createItems } from "../../API/items/itemsAPI";
import { getKB } from "../../API/KB/KBAPI";
import Loader from "../../Loaders/LocalLoader";
import { isValidURL } from "../../utils";
import ProcessorFunction from "./ProcessorFunction";
import { splitArrays, parseUrls, extractPages, readSitemap, readRobots, createPageType } from "./ImportUtils";
import { renderTemplate } from "../ItemHelpers";
import { FixedSizeList as List } from 'react-window';

const STATUS = {
    ENTER_WEBSITE: 0,
    PARSE_URLS: 1,
    EXTRACT_PAGES: 2,
    IMPORT_PAGES: 3,
    SAVE_PAGES: 4,
    PARSE_URLS_RETRY: 10,
};

const getSteps = () => {
    return [
        "Enter Website",
        "Select URLs",
        "Extract pages",
        "Review/Edit pages",
        "Save to database",
    ];
};

const getNextButton = (status, sitemaps, sitemapURL, parseURLsFilterDone, importPagesFilterDone) => {
    if (status === STATUS.PARSE_URLS && !parseURLsFilterDone  || status === STATUS.IMPORT_PAGES && !importPagesFilterDone) {
        return status === STATUS.PARSE_URLS ? 'Filter URLs' : 'Process Pages'
    }

    if (status === STATUS.ENTER_WEBSITE) {
        return `Next`
    }
    else if (status === STATUS.IMPORT_PAGES || status === STATUS.SAVE_PAGES) {
        return "Save"
    }
    else if (status === STATUS.PARSE_URLS_RETRY) {
        return `Retry failed`
    }
    else {
        return `Next`
    }

}

const ImportPagesPopup = ({ importOpen, setImportOpen, kbId, KB, onClose = () => { } }) => {
    const [textAreaData, setTextAreaData] = useState("");
    const [parseURLsFilterDone, setParseURLsFilterDone] = useState(false);
    const [importPagesFilterDone, setImportPagesFilterDone] = useState(false);
    const [urlArray, setUrlArray] = useState([]);
    const [isLoadingBlocking, setIsLoadingBlocking] = useState(false);
    const [batchResponses, setBatchResponses] = useState([]);
    const [status, setStatus] = useState(STATUS.ENTER_WEBSITE);
    const [progress, setProgress] = useState(0);
    const [failedURLs, setFailedURLs] = useState([]);
    const [sitemaps, setSitemaps] = useState([]);
    const [sitemapURL, setSitemapURL] = useState('');
    const [readWebpagesConcurrencyLimit, setReadWebpagesConcurrencyLimit] = useState(10);
    const [embeddingsConcurrencyLimit, setEmbeddingsConcurrencyLimit] = useState(10);
    const [embeddingTemplate, setEmbeddingTemplate] = useState("${item.title} ${item.description} ${item.content}");
    const parseRef = useRef();
    const importRef = useRef();

    useEffect(() => {
        if (sitemaps?.length && !isValidURL(textAreaData)) {
            setSitemaps([])
            setSitemapURL('');
        }
    }, [textAreaData, sitemaps, sitemapURL]);

    const handleNext = async () => {

        if (status === STATUS.PARSE_URLS && !parseURLsFilterDone  || status === STATUS.IMPORT_PAGES && !importPagesFilterDone) {
            if (status === STATUS.PARSE_URLS) parseRef.current.click()
            if (status === STATUS.IMPORT_PAGES) importRef.current.click()
        }
        else if (status === STATUS.PARSE_URLS_RETRY) { // -> goto PARSE_URLS  1
            setStatus(STATUS.PARSE_URLS)
        }
        else if (status === STATUS.PARSE_URLS) { // -> goto EXTRACT_PAGES 2
            handleExtract();
        }
        else if (status === STATUS.IMPORT_PAGES) { // -> goto SAVE_PAGES 4
            handleImport();
        }
        else if (status === STATUS.ENTER_WEBSITE) { // -> goto SAVE_PAGES 4
            setIsLoadingBlocking(true)
            if (!sitemaps?.length) {
                const data = await readRobots(textAreaData, KB);

                let sitemapURLs = data?.data?.[0]?.content?.split('\n')
                    .filter(line => line.startsWith('Sitemap:'))
                    .map(line => line.replace('Sitemap: ', '').trim()) || [];

                sitemapURLs.unshift('Will provide URLs manually')

                if (sitemapURLs?.length) {
                    setSitemaps(sitemapURLs)
                    setSitemapURL(sitemapURLs[sitemapURLs?.length > 1 ? 1 : 0])
                }
            } else if (sitemaps && sitemapURL) {
                if (isValidURL(sitemapURL)) {
                    const data = await readSitemap(sitemapURL, KB)
                    setStatus(STATUS.PARSE_URLS)
                    setTextAreaData(data?.sites?.length ? data?.sites?.join('\n') : '')
                    setUrlArray(data?.sites);
                } else {
                    setStatus(STATUS.PARSE_URLS)
                    setTextAreaData('')
                }

            }
            setIsLoadingBlocking(false)
        }

    };

    const steps = getSteps();
    const activeStep = status;

    const init = (input = '', myStatus = STATUS.ENTER_WEBSITE) => {
        setParseURLsFilterDone(false)
        setImportPagesFilterDone(false)
        setTextAreaData(input);
        setUrlArray([]);
        setBatchResponses([]);
        setStatus(myStatus);
        setProgress(0);
        setFailedURLs([])
    }

    useEffect(() => {
        if (batchResponses.length > 0) {
            setTextAreaData(JSON.stringify(batchResponses.filter((element) => element !== null), null, 2));
        }

        if (progress === 100) {
            setProgress(0);
            return;
        }

        if (status === STATUS.EXTRACT_PAGES) {
            let percentage = batchResponses.length === 0 ? 0 : (batchResponses.length / urlArray.length) * 100;

            if (percentage) {
                setProgress(percentage);
            }
        }

    }, [batchResponses, status]);

    const processBatches = async (batches) => {
        let count = 0;
        for (const batch of batches) {
            count++;

            setIsLoadingBlocking(true)
            const result = await extractPages(batch, KB);
            setIsLoadingBlocking(false)

            const failedBatch = !result?.data || result.data.find((o) => !o?.url);
            let newBatchResponses = [];

            if (failedBatch) {
                setFailedURLs(prev => {
                    return [...prev, ...batch.map(o => o.url)];
                });
            } else {
                newBatchResponses = result.data
                    .filter((o) => o?.url)
                    .map(result => {
                        if (result) {
                            const { price, ...rest } = result;
                            return {
                                ...rest,
                                price: price ? price : undefined
                            }
                        }
                        return null;
                    })
                    .filter((result) => result?.url);
            }

            setBatchResponses((prevResponses) => [
                ...prevResponses,
                ...newBatchResponses
            ]);

            if (count === batches.length) {
                setStatus(STATUS.IMPORT_PAGES);
                setProgress(100);
            }
        }
    };

    const handleImport = async () => {
        setStatus(STATUS.SAVE_PAGES);
        setProgress(0);

        try {
            let KBData = deepClone(KB);

            // Create page type if missing
            if (!KB?.itemTypes?.page) {
                await createPageType(embeddingTemplate);
                KBData = await getKB();
            }

            const { embeddingModel, embeddingDimension } = KB;
            const items = [];
            const seen = {};
            const queue = [];
            let c = 0;

            const processBatchEmbedding = async (res) => {
                if (!res || seen[res.url]) return;
                seen[res.url] = 1;

                let myEmbeddingTemplate = KB?.itemTypes?.page?.embeddingTemplate
                    ? KB?.itemTypes.page.embeddingTemplate
                    : embeddingTemplate;

                const embeddingChunk = renderTemplate(myEmbeddingTemplate, res);

                const retryCreateEmbeddingItem = async (chunk, retries = 3, delays = [5000, 10000, 15000]) => {
                    try {
                        return await createEmbeddingItem(chunk.substring(0, 8000), KBData);
                    } catch (error) {
                        if (retries > 0) {
                            const delay = delays[3 - retries];
                            await new Promise(resolve => setTimeout(resolve, delay));
                            return retryCreateEmbeddingItem(chunk, retries - 1, delays);
                        } else {
                            throw error;
                        }
                    }
                };

                try {
                    const { totalTokens, embeddings } = await retryCreateEmbeddingItem(embeddingChunk);
                    const item = {
                        url: res.url,
                        title: res.title,
                        content: res.content,
                        description: res.description,
                        image: res.image,
                        attributes: KBData.itemTypes.page.attributes,
                        embeddingModel,
                        embeddingDimension,
                        itemType: 'page',
                        totalTokens,
                        embeddings
                    };

                    if (res?.price) item.price = res.price;
                    if (res?.currency) item.currency = res.currency;

                    items.push(item);

                    c++;
                    setProgress((c / batchResponses.length) * 90);
                    return embeddings ? { url: res.url, success: true } : { url: res.url, success: false }
                } catch (error) {
                    console.error('Error processing batch:', error);
                    return { url: res.url, success: false };
                }
            };

            const processQueue = async () => {
                while (queue.length > 0) {
                    const promises = queue.splice(0, embeddingsConcurrencyLimit).map(res => processBatchEmbedding(res));
                    await Promise.all(promises);
                }
            };

            for (let res of batchResponses) {
                queue.push(res);
            }

            await processQueue();

            const failedEmbeddingsURLs = items.filter(item => !item.embeddings).map(item => item.url);

            let failed = [];
            if (failedEmbeddingsURLs.length) {
                failed = failedEmbeddingsURLs;
                setFailedURLs(prev => [...prev, ...failed]);
            }

            const myFailedURLs = [...failedURLs, ...failed];

            const batchSize = 50;
            let i = 0;
            while (i < items.length) {
                const batch = items.slice(i, i + batchSize);
                await createItems(KB, batch);
                i += batchSize;
            }

            setProgress(100);

            if (myFailedURLs.length) {
                init(myFailedURLs.join(`\n`), STATUS.PARSE_URLS_RETRY);
                handleParseURLs(myFailedURLs.join(`\n`));
            } else {
                init();
                setImportOpen(false);
                onClose();
            }
        } catch (error) {
            alert(error);
            console.error('Error saving item:', error);
        }
    };

    const handleExtract = () => {
        setStatus(STATUS.EXTRACT_PAGES)
        setTextAreaData(`Loading ...`);
        const batches = splitArrays(urlArray.map((o) => ({ url: o })), readWebpagesConcurrencyLimit);
        processBatches(batches);
    };

    const handleClose = () => {
        init();
        setImportOpen(false);
        onClose();
    };

    const handleParseURLs = async (value) => {
        setTextAreaData(value)

        setIsLoadingBlocking(true)

        const { urls, content } = await parseUrls(value, KB);

        setIsLoadingBlocking(false)

        setUrlArray(urls);

        if (content) {
            setTextAreaData(content);
        }
    }

    const renderRow = ({ index, style }) => (
        <div style={style}>
            {textAreaData.split('\n')[index]}
        </div>
    );

    return (
        <Dialog open={importOpen} onClose={handleClose} fullScreen>

            <Loader open={isLoadingBlocking || status === STATUS.EXTRACT_PAGES || status === STATUS.SAVE_PAGES} blocking={true} />

            <Box sx={{ width: "100%" }}>
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    color="secondary"
                    style={{ height: 6 }}
                />
            </Box>
            <DialogTitle sx={{ backgroundColor: colorPaper }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label, index) => {
                        return (
                            <Step
                                sx={{
                                    '& .MuiStepLabel-root .Mui-completed': { color: 'secondary.main' },
                                    '& .MuiStepLabel-root .Mui-active': { color: 'secondary.main' }
                                }}
                                key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: isMobile ? 'center' : 'space-between',
                        marginBottom: '16px',
                        marginTop: '16px',
                        gap: isMobile ? '16px' : '0',
                        height: '40px'
                    }}
                >
                    <Button
                        variant="outlined"
                        sx={{ ...styleSecondaryButton, marginRight: isMobile ? '0' : '16px', minWidth: 200 }}
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>


                    {status === STATUS.PARSE_URLS && <FormControl variant="outlined" sx={{ minWidth: 200, marginRight: isMobile ? '0' : '16px' }}>
                        <InputLabel>Read Webpages Concurrency</InputLabel>
                        <Select sx={{ height: '40px' }}
                                value={readWebpagesConcurrencyLimit}
                                onChange={(e) => setReadWebpagesConcurrencyLimit(e.target.value)}
                                label="Read Webpages Concurrency"
                        >
                            {[1, 2, 3, 4, 5, 10, 15, 20, 25, 50, 100].map((value) => (
                                <MenuItem key={value} value={value}>
                                    {value}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>}

                    {status === STATUS.PARSE_URLS && <FormControl variant="outlined" sx={{ minWidth: 200, marginRight: isMobile ? '0' : '16px' }}>
                        <InputLabel>Create Embeddings Concurrency</InputLabel>
                        <Select sx={{ height: '40px' }}
                                value={embeddingsConcurrencyLimit}
                                onChange={(e) => setEmbeddingsConcurrencyLimit(e.target.value)}
                                label="Create Embeddings Concurrency"
                        >
                            {[1, 2, 3, 4, 5, 10, 15, 20, 25, 50, 100].map((value) => (
                                <MenuItem key={value} value={value}>
                                    {value}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>}

                    {<Button
                        variant="contained"
                        sx={{ minWidth: 200, position: "relative", height: '40px' }}
                        onClick={handleNext}
                        color="primary"
                        disabled={
                            status !== STATUS.PARSE_URLS_RETRY &&
                            (!(status === STATUS.ENTER_WEBSITE && isValidURL(textAreaData))) &&
                            (
                                status === STATUS.EXTRACT_PAGES ||
                                status === STATUS.SAVE_PAGES ||
                                !urlArray?.length && !batchResponses?.length
                            )
                        }
                    >
                        {getNextButton(status, sitemaps, sitemapURL, parseURLsFilterDone, importPagesFilterDone)}
                    </Button>}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '8px', marginTop: '8px' }}>
                    {status === STATUS.PARSE_URLS && (urlArray.length ?
                        <span><b>{urlArray.length}</b> unique URLs detected</span> : '')}
                    {status === STATUS.EXTRACT_PAGES &&
                        <span>Extracted {batchResponses.length} of {urlArray.length} pages ...</span>}
                    {status === STATUS.IMPORT_PAGES && batchResponses.length > 0 &&
                        <span><b>{batchResponses.length}</b> pages extracted</span>}
                    {status === STATUS.SAVE_PAGES && batchResponses.length > 0 &&
                        <span>Create Embeddings and Save {batchResponses.length} pages ...</span>}
                </Box>

            </DialogTitle>

            <DialogContent sx={{ backgroundColor: colorPaper }}>
                {status !== STATUS.IMPORT_PAGES && status !== STATUS.EXTRACT_PAGES && status !== STATUS.SAVE_PAGES && <TextField
                    style={{ marginTop: 10, maxHeight: 400, overflow: 'auto' }}
                    label={status !== STATUS.ENTER_WEBSITE ? "Paste URLs" : "Enter Website URL or sitemap"}
                    placeholder={status !== STATUS.ENTER_WEBSITE ? "" : "https://example.com"}
                    value={textAreaData}
                    onChange={async (e) => {
                        if (status === STATUS.PARSE_URLS || status === STATUS.PARSE_URLS_RETRY) {
                            handleParseURLs(e.target.value)
                        } else if (status === STATUS.IMPORT_PAGES) {
                            // if (parseJSON(e.target.value)) {
                            //     setBatchResponses(parseJSON(e.target.value));
                            //     setTextAreaData(e.target.value)
                            // } else {
                            //     setTextAreaData(e.target.value)
                            // }
                        } else if (status === STATUS.ENTER_WEBSITE) {
                            let value = e.target.value
                            if (value.length >= 4 && !value.startsWith('http')) {
                                value = 'https://' + value;
                            }

                            if (value.endsWith('.xml')) {
                                setSitemapURL(value)
                                setSitemaps([value]);
                            } else {
                                setSitemapURL('')
                                setSitemaps([]);
                            }
                            setTextAreaData(value)
                        }
                    }}
                    fullWidth
                    multiline={status !== STATUS.ENTER_WEBSITE}
                    minRows={status !== STATUS.ENTER_WEBSITE ? 20 : 1}
                    required
                    variant="outlined"
                    InputLabelProps={{
                        style: { marginTop: 5 } // Adjust this value as needed
                    }}
                />}

                {(status === STATUS.IMPORT_PAGES || status === STATUS.SAVE_PAGES) && (
                    <Box mt={2} style={{ height: 400, overflow: 'auto' }}>
                        {textAreaData.split('\n').length > 100 ? (
                            <List
                                height={400}
                                itemCount={textAreaData.split('\n').length}
                                itemSize={35}
                                width="100%"
                            >
                                {renderRow}
                            </List>
                        ) : (
                            textAreaData.split('\n').map((line, index) => (
                                <div key={index}>
                                    {line}
                                </div>
                            ))
                        )}
                    </Box>
                )}

                {status === STATUS.ENTER_WEBSITE && !!sitemaps?.length && isValidURL(textAreaData) &&
                    <FormControl variant="outlined" required={true} sx={{ minWidth: '100%', mt: 4, marginRight: isMobile ? '0' : '16px' }}>
                        <InputLabel>Choose Sitemap</InputLabel>
                        <Select fullWidth={true} value={sitemapURL} onChange={(e) => setSitemapURL(e.target.value)} label="Choose Sitemap">
                            {sitemaps.map((value) => (<MenuItem key={value} value={value}>{value}</MenuItem>))}
                        </Select>
                    </FormControl>}

                {status === STATUS.IMPORT_PAGES && !KB?.itemTypes?.page && <Box mt={2}  >
                    <Tooltip
                        title="Specify a template for page attributes to be stored as embeddings for semantic searchability. Example: ${item.title} ${item.description}"
                        placement="bottom-end">
                        <TextField
                            autoComplete="off"
                            label="Search Embedding Template"
                            value={embeddingTemplate || ''}
                            onChange={(e) => setEmbeddingTemplate(e.target.value)}
                            fullWidth
                            variant="outlined"
                            placeholder="${item.title} ${item.description}"
                        />
                    </Tooltip>
                </Box>}

                {(status === STATUS.PARSE_URLS || status === STATUS.IMPORT_PAGES) &&
                    <ProcessorFunction {...{ setUrlArray, STATUS, status, urlArray, setTextAreaData,
                        batchResponses, setBatchResponses, setImportPagesFilterDone, setParseURLsFilterDone, parseRef, importRef }} />}

            </DialogContent>
        </Dialog>
    );
};

export default ImportPagesPopup;